// Mantine's NumberInput returns values as `string | number`. Fully parsed numbers
// come through as numbers, but certain states are represented as strings:
//   - Empty input -> ""
//   - Values beyond JS safe integer range -> e.g. "90071992547409910"
//   - Intermediate zero-only inputs -> e.g. "0.", "0.0", "-0.00"
// Centralise the helpers here so components can consistently coerce and validate
// these edge cases.

type NumberFieldValidationOptions = {
  fieldLabel: string;
  allowZero: boolean;
  integerOnly?: boolean;
};

type NumberFieldValidationSuccess = {
  ok: true;
  value: number;
};

type NumberFieldValidationFailure = {
  ok: false;
  message: string;
};

export type NumberFieldValidationResult = NumberFieldValidationSuccess | NumberFieldValidationFailure;

const isFiniteNumber = (value: number | undefined): value is number => typeof value === "number" && Number.isFinite(value);

const getNumberRequirementMessage = (fieldLabel: string, allowZero: boolean) =>
  `${fieldLabel} must be ${allowZero ? "zero or a positive" : "a positive"} number.`;

const getIntegerRequirementMessage = (fieldLabel: string) => `${fieldLabel} must be a whole number.`;

export const toOptionalNumber = (value: string | number): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (value === "") return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const validateNumberField = (
  value: number | undefined,
  { fieldLabel, allowZero, integerOnly }: NumberFieldValidationOptions
): NumberFieldValidationResult => {
  if (!isFiniteNumber(value)) {
    return { ok: false, message: getNumberRequirementMessage(fieldLabel, allowZero) };
  }

  if (allowZero ? value < 0 : value <= 0) {
    return { ok: false, message: getNumberRequirementMessage(fieldLabel, allowZero) };
  }

  if (integerOnly && !Number.isInteger(value)) {
    return { ok: false, message: getIntegerRequirementMessage(fieldLabel) };
  }

  return { ok: true, value };
};

type NumberFieldDescriptor<Field extends string> = {
  field: Field;
  value: number | undefined;
} & NumberFieldValidationOptions;

type MultipleNumberFieldValidationSuccess<Field extends string> = {
  ok: true;
  values: Record<Field, number>;
};

type MultipleNumberFieldValidationFailure<Field extends string> = {
  ok: false;
  field: Field;
  message: string;
};

export type MultipleNumberFieldValidationResult<Field extends string> =
  | MultipleNumberFieldValidationSuccess<Field>
  | MultipleNumberFieldValidationFailure<Field>;

export const validateNumberFields = <Field extends string>(
  descriptors: NumberFieldDescriptor<Field>[]
): MultipleNumberFieldValidationResult<Field> => {
  const values = {} as Record<Field, number>;

  for (const descriptor of descriptors) {
    const { field, value, ...options } = descriptor;
    const result = validateNumberField(value, options);

    if (!result.ok) {
      return { ok: false, field, message: result.message };
    }

    values[field] = result.value;
  }

  return { ok: true, values };
};
